import { useTranslation } from '@frontend/i18n';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { theme } from '@frontend/theme';
import {
  Checkbox,
  Text,
  PopoverMenu,
  PopoverMenuItem,
  SearchField,
  useFormField,
  usePopoverMenu,
  Button,
} from '@frontend/design-system';

type SmsTagsFilterPopoverContentProps = {
  faxNumbers: string[];
  selectedFaxNumbers: string[];
  selectFaxNumbers: (faxNumbers: string[]) => void;
  getMenuProps: ReturnType<typeof usePopoverMenu<HTMLButtonElement>>['getMenuProps'];
  getItemProps: ReturnType<typeof usePopoverMenu<HTMLButtonElement>>['getItemProps'];
};

export const FaxNumbersSelectPopover = ({
  faxNumbers,
  selectFaxNumbers,
  selectedFaxNumbers,
  getMenuProps,
  getItemProps,
}: SmsTagsFilterPopoverContentProps) => {
  const { t } = useTranslation('fax');
  const searchFieldProps = useFormField({ type: 'text' });

  const filteredFaxNumbers = searchFieldProps.value
    ? faxNumbers.filter((number) => number.includes(searchFieldProps.value))
    : faxNumbers;

  return (
    <PopoverMenu {...getMenuProps()}>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 0,
          disableCloseOnSelect: true,
        })}
        css={{
          maxWidth: 220,
          paddingTop: theme.spacing(1),
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <SearchField
          {...searchFieldProps}
          name='fax-number-search'
          onKeyDownCapture={(e) => {
            e.stopPropagation();
          }}
        />
      </PopoverMenuItem>
      <PopoverMenuItem
        as='span'
        {...getItemProps({
          index: 1,
          disableCloseOnSelect: true,
        })}
        css={{
          marginTop: theme.spacing(1),
          padding: theme.spacing(1, 0.75),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <Button
          variant='secondary'
          css={{ fontWeight: 'bold', color: theme.colors.primary50, border: 'none' }}
          onClick={() => selectFaxNumbers(filteredFaxNumbers)}
          disabled={!filteredFaxNumbers.length}
        >
          {t('Select all')}
        </Button>
        <Button
          variant='secondary'
          css={{
            fontWeight: 'bold',
            color: theme.colors.primary50,
            border: 'none',
          }}
          onClick={() => {
            selectFaxNumbers([]);
          }}
          disabled={!selectedFaxNumbers?.length}
        >
          {t('Clear')}
        </Button>
      </PopoverMenuItem>
      <div css={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: 240 }}>
        {!filteredFaxNumbers.length ? (
          <Text textAlign='center' css={{ paddingTop: theme.spacing(1) }}>
            No matching numbers
          </Text>
        ) : (
          filteredFaxNumbers.map((number, i) => {
            return (
              <PopoverMenuItem
                key={number}
                as='span'
                {...getItemProps({
                  index: i + 2,
                  disableCloseOnSelect: true,
                  onClick: () => {
                    selectFaxNumbers([number]);
                  },
                })}
                css={{
                  width: '100%',
                  height: 'auto',
                  padding: theme.spacing(1, 2),
                  cursor: 'pointer',
                  svg: { fill: theme.colors.white },
                }}
              >
                <Checkbox
                  id={number}
                  value={!!selectedFaxNumbers?.includes(number)}
                  onChange={() => {}}
                  onFocus={() => {}}
                  onBlur={() => {}}
                  aria-invalid={false}
                  active={false}
                  error={false}
                  name={number}
                />

                <Text>{formatPhoneNumber(number, true)}</Text>
              </PopoverMenuItem>
            );
          })
        )}
      </div>
    </PopoverMenu>
  );
};
